import { useState, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronLeft, RotateCcw } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'

// ── Types ──────────────────────────────────────────────────────
interface RawPrize {
  id: string
  name: string
  amount: number
  number: string[]
  reward: string
}

interface LotteryLatest {
  draw_date: string
  first_prize: number | null
  raw_data: {
    prizes?: RawPrize[]
    runningNumbers?: RawPrize[]
  } | null
}

// ── Helpers ────────────────────────────────────────────────────
const THAI_MONTHS = [
  'ม.ค.','ก.พ.','มี.ค.','เม.ย.',
  'พ.ค.','มิ.ย.','ก.ค.','ส.ค.',
  'ก.ย.','ต.ค.','พ.ย.','ธ.ค.',
]

function formatThaiDate(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number)
  return `${d} ${THAI_MONTHS[m - 1]} ${y + 543}`
}

function formatReward(reward: string): string {
  const n = parseInt(reward)
  if (isNaN(n)) return reward
  return n.toLocaleString('th-TH') + ' บาท'
}

// ── Prize check logic ──────────────────────────────────────────
interface PrizeResult {
  id: string
  name: string
  reward: string
  matched: boolean
  yourNum: string        // portion of input to compare
  winNums: string[]      // winning numbers array
  manyNumbers: boolean   // > 10 → don't list all when not matched
}

const PRIZE_ORDER = [
  { id: 'prizeFirst',     type: 'prize',   digits: 6 },
  { id: 'prizeFirstNear', type: 'prize',   digits: 6 },
  { id: 'prizeSecond',    type: 'prize',   digits: 6 },
  { id: 'prizeThird',     type: 'prize',   digits: 6 },
  { id: 'prizeForth',     type: 'prize',   digits: 6 },
  { id: 'prizeFifth',     type: 'prize',   digits: 6 },
  { id: 'runningNumberFrontThree', type: 'running', digits: 3 },
  { id: 'runningNumberBackThree',  type: 'running', digits: 3 },
  { id: 'runningNumberBackTwo',    type: 'running', digits: 2 },
]

function checkNumber(input: string, data: LotteryLatest): PrizeResult[] {
  const six = input.padStart(6, '0')
  const prizes    = data.raw_data?.prizes        ?? []
  const running   = data.raw_data?.runningNumbers ?? []
  const allRaw    = [...prizes, ...running]

  return PRIZE_ORDER.map(({ id, digits }) => {
    const raw = allRaw.find(p => p.id === id)
    if (!raw) return null

    // slice input to the digits we care about
    let yourNum: string
    if (digits === 6) yourNum = six
    else if (digits === 3 && id.includes('Front')) yourNum = six.slice(0, 3)
    else if (digits === 3) yourNum = six.slice(3, 6)
    else yourNum = six.slice(4, 6)      // 2 digits

    const winNums   = raw.number
    const matched   = winNums.includes(yourNum)
    const manyNumbers = winNums.length > 10

    return {
      id,
      name: raw.name,
      reward: formatReward(raw.reward),
      matched,
      yourNum,
      winNums,
      manyNumbers,
    } satisfies PrizeResult
  }).filter(Boolean) as PrizeResult[]
}

// ── Sub-components ─────────────────────────────────────────────
function PrizeRow({ prize }: { prize: PrizeResult }) {
  const showWinNums = !prize.manyNumbers || prize.matched

  return (
    <div className="rounded-2xl p-4"
         style={{
           background: prize.matched ? 'rgba(184,148,90,0.08)' : 'var(--color-surface)',
           border: '1.5px solid ' + (prize.matched ? 'var(--color-gold)' : 'var(--color-surface-3)'),
         }}>
      <div className="flex items-start gap-3">
        {/* Icon */}
        <span className="text-xl shrink-0 mt-0.5">
          {prize.matched ? '✅' : '❌'}
        </span>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="font-semibold text-sm" style={{ color: 'var(--color-text)' }}>
              {prize.name}
            </p>
            {prize.matched && (
              <span className="font-bold text-sm shrink-0" style={{ color: 'var(--color-gold-dark)' }}>
                {prize.reward}
              </span>
            )}
          </div>

          {/* Your number */}
          <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
            {'เลขของคุณ: '}
            <span className="font-mono font-bold"
                  style={{ color: prize.matched ? 'var(--color-gold-dark)' : 'inherit' }}>
              {prize.yourNum}
            </span>
          </p>

          {/* Winning numbers */}
          {showWinNums ? (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {prize.winNums.map((n, i) => (
                <span key={i}
                      className="text-xs font-mono px-1.5 py-0.5 rounded-md"
                      style={{
                        background: n === prize.yourNum
                          ? 'var(--color-gold)'
                          : 'var(--color-surface-2)',
                        color: n === prize.yourNum ? '#fff' : 'var(--color-text-muted)',
                        fontWeight: n === prize.yourNum ? 700 : 400,
                      }}>
                  {n}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-xs mt-1" style={{ color: 'var(--color-text-soft)' }}>
              {'ผล: '}({prize.winNums.length}{' เลข'})
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Main page ──────────────────────────────────────────────────
export default function CheckPage() {
  const navigate = useNavigate()
  const [digits, setDigits] = useState(['', '', '', '', '', ''])
  const [result, setResult] = useState<PrizeResult[] | null>(null)
  const refs = useRef<(HTMLInputElement | null)[]>([])

  const { data, isLoading, isError } = useQuery({
    queryKey: ['lottery', 'check', 'latest'],
    queryFn: () => api.lottery.latest('government') as Promise<LotteryLatest>,
    staleTime: 1000 * 60 * 10,
  })

  const inputFull = digits.every(d => d !== '')
  const inputStr  = digits.join('')
  const wonPrizes = result?.filter(r => r.matched) ?? []
  const hasWin    = wonPrizes.length > 0

  function handleDigit(i: number, val: string) {
    if (!/^\d*$/.test(val)) return
    const next = [...digits]
    next[i] = val.slice(-1)
    setDigits(next)
    setResult(null)
    if (val && i < 5) refs.current[i + 1]?.focus()
  }

  function handleKeyDown(i: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace') {
      if (digits[i]) {
        const next = [...digits]
        next[i] = ''
        setDigits(next)
        setResult(null)
      } else if (i > 0) {
        refs.current[i - 1]?.focus()
      }
    }
  }

  function handlePaste(e: React.ClipboardEvent) {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (!pasted) return
    const next = ['', '', '', '', '', '']
    for (let k = 0; k < pasted.length; k++) next[k] = pasted[k]
    setDigits(next)
    setResult(null)
    refs.current[Math.min(pasted.length, 5)]?.focus()
  }

  function handleCheck() {
    if (!inputFull || !data) return
    setResult(checkNumber(inputStr, data))
  }

  function handleReset() {
    setDigits(['', '', '', '', '', ''])
    setResult(null)
    setTimeout(() => refs.current[0]?.focus(), 50)
  }

  return (
    <div className="flex flex-col min-h-dvh" style={{ background: 'var(--color-bg)' }}>

      {/* ── Header ─────────────────────────────── */}
      <div className="px-4 pt-6 pb-4 flex items-center gap-3">
        <button onClick={() => navigate(-1)} style={{ color: 'var(--color-text-muted)' }}>
          <ChevronLeft size={24} />
        </button>
        <div>
          <h1 className="text-lg font-bold" style={{ color: 'var(--color-text)' }}>
            {'ตรวจลอตเตอรี่'}
          </h1>
          {data && !isLoading && (
            <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
              {'งวดวันที่ '}{formatThaiDate(data.draw_date)}
            </p>
          )}
        </div>
      </div>

      <div className="flex-1 px-4 flex flex-col gap-4 pb-6">

        {/* ── Skeleton ───────────────────────── */}
        {isLoading && (
          <div className="card-amulet p-5 animate-pulse flex flex-col gap-3 items-center">
            <div className="h-3 rounded w-1/3" style={{ background: 'var(--color-surface-2)' }} />
            <div className="flex gap-2">
              {[0,1,2,3,4,5].map(i => (
                <div key={i} className="w-12 h-14 rounded-xl" style={{ background: 'var(--color-surface-2)' }} />
              ))}
            </div>
            <div className="h-12 w-full rounded-xl" style={{ background: 'var(--color-surface-2)' }} />
          </div>
        )}

        {/* ── Error ──────────────────────────── */}
        {isError && (
          <div className="card-amulet p-5 text-center">
            <p className="text-sm" style={{ color: 'var(--color-danger)' }}>
              {'ไม่สามารถโหลดข้อมูลลอตเตอรี่ได้'}
            </p>
          </div>
        )}

        {/* ── Input card ─────────────────────── */}
        {!isLoading && !isError && data && (
          <div className="card-amulet p-5">
            <p className="text-sm font-medium text-center mb-4"
               style={{ color: 'var(--color-text-muted)' }}>
              {'กรอกเลขสลาก 6 หลัก'}
            </p>

            <div className="flex justify-center gap-2 mb-5" onPaste={handlePaste}>
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={el => { refs.current[i] = el }}
                  type="tel"
                  inputMode="numeric"
                  maxLength={2}
                  value={d}
                  onChange={e => handleDigit(i, e.target.value)}
                  onKeyDown={e => handleKeyDown(i, e)}
                  onFocus={e => e.target.select()}
                  className="w-12 h-14 text-center text-2xl font-mono font-bold rounded-xl outline-none transition-all"
                  style={{
                    background: d ? 'var(--color-primary)' : 'var(--color-surface-2)',
                    color: d ? '#fff' : 'var(--color-text-soft)',
                    border: '2px solid ' + (d ? 'var(--color-primary)' : 'var(--color-surface-3)'),
                  }}
                />
              ))}
            </div>

            <button
              onClick={handleCheck}
              disabled={!inputFull}
              className="w-full py-3.5 rounded-xl font-bold text-base btn-gold shimmer disabled:opacity-40 disabled:cursor-not-allowed">
              {'ตรวจสอบ'}
            </button>
          </div>
        )}

        {/* ── Results ────────────────────────── */}
        {result && (
          <div className="flex flex-col gap-3">

            {/* Win / No-win banner */}
            {hasWin ? (
              <div className="rounded-2xl p-5 text-center"
                   style={{
                     background: 'var(--gradient-gold)',
                     boxShadow: '0 4px 20px rgba(184,148,90,0.4)',
                   }}>
                <div className="text-4xl mb-2">{'🎉'}</div>
                <p className="text-white font-bold text-xl">{'ยินดีด้วย!'}</p>
                <p className="text-white/90 text-sm mt-1">
                  {'เลข '}{inputStr}{' ถูก '}{wonPrizes.length}{' รางวัล'}
                </p>
                <div className="flex flex-wrap justify-center gap-2 mt-3">
                  {wonPrizes.map(p => (
                    <span key={p.id}
                          className="text-xs font-semibold px-3 py-1 rounded-full"
                          style={{ background: 'rgba(255,255,255,0.25)', color: '#fff' }}>
                      {p.name} — {p.reward}
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <div className="card-amulet p-5 text-center">
                <div className="text-3xl mb-2">{'🍀'}</div>
                <p className="font-bold text-base" style={{ color: 'var(--color-text)' }}>
                  {'ไม่ถูกรางวัลในงวดนี้'}
                </p>
                <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
                  {'ลองทำนายฝันเพื่อหาเลขมงคลใหม่'}
                </p>
                <button
                  onClick={() => navigate('/dream')}
                  className="mt-3 px-5 py-2 rounded-xl text-sm font-semibold btn-gold shimmer">
                  {'ทำนายฝัน'}
                </button>
              </div>
            )}

            {/* Prize rows */}
            {result.map(prize => (
              <PrizeRow key={prize.id} prize={prize} />
            ))}

            {/* Reset */}
            <button
              onClick={handleReset}
              className="flex items-center justify-center gap-2 py-3 rounded-xl font-medium text-sm"
              style={{
                background: 'var(--color-surface)',
                border: '1px solid var(--color-surface-3)',
                color: 'var(--color-text-muted)',
              }}>
              <RotateCcw size={15} />
              {'ตรวจเลขใหม่'}
            </button>
          </div>
        )}
      </div>

      <p className="text-center text-xs py-3 px-4" style={{ color: 'var(--color-text-soft)' }}>
        {'ข้อมูลเพื่อการอ้างอิง — กรุณาตรวจสอบกับสลากจริงด้วย'}
      </p>
    </div>
  )
}
